import { useState } from 'react';
import {
  Title,
  Button,
  Table,
  Image,
  Text,
  Stack,
  Group,
  Paper,
  Card,
  NumberInput,
  Modal,
  Alert,
  Loader,
  Center,
  Pagination,
  ThemeIcon,
  Anchor,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconRefresh, IconPackage, IconAlertCircle, IconCheck, IconSearch, IconX } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';

interface Product {
  id: string;
  barcode: string;
  name: string;
  brand?: string;
  category?: string;
  imageUrl?: string;
  reviewCount: number;
  averageRating?: number;
  createdAt: string;
}

interface SeedResult {
  addedCount: number;
  skippedCount: number;
  addedProducts: { id: string; barcode: string; name: string; brand?: string }[];
  skippedReasons: string[];
}

interface ProductsResponse {
  products: Product[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export default function Products() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [seedModalOpen, setSeedModalOpen] = useState(false);
  const [seedResult, setSeedResult] = useState<SeedResult | null>(null);

  const seedForm = useForm({
    initialValues: {
      pages: 3,
      pageSize: 100,
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin-products', page, debouncedSearch],
    queryFn: () => {
      const endpoint = debouncedSearch.trim() ? '/products/browse' : '/products';
      const params: Record<string, unknown> = { page, pageSize: 20 };
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      return api.get<ProductsResponse>(endpoint, { params }).then((r) => r.data);
    },
    staleTime: 30_000,
  });

  const products = data?.products ?? [];
  const pagination = {
    current: data?.page ?? page,
    pageSize: data?.pageSize ?? 20,
    total: data?.totalCount ?? 0,
    totalPages: data?.totalPages ?? 1,
  };

  const seedMutation = useMutation({
    mutationFn: (values: { pages: number; pageSize: number }) =>
      api
        .post<SeedResult>('/admin/products/seed-from-openfoodfacts', null, {
          params: { pages: values.pages, pageSize: values.pageSize },
        })
        .then((r) => r.data),
    onSuccess: (result) => {
      setSeedResult(result);
      notifications.show({
        title: 'Success',
        message: `Added ${result.addedCount} products!`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to seed products',
        color: 'red',
      });
    },
  });

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleSeedProducts = () => {
    setSeedResult(null);
    seedMutation.mutate(seedForm.values);
  };

  const rows = products.map((product) => (
    <Table.Tr key={product.id}>
      <Table.Td>
        {product.imageUrl ? (
          <Image src={product.imageUrl} w={50} h={50} radius="sm" fit="contain" />
        ) : (
          <ThemeIcon size={50} radius="sm" variant="light" color="gray">
            <IconPackage size={24} />
          </ThemeIcon>
        )}
      </Table.Td>
      <Table.Td>
        <Anchor component={Link} to={`/admin/products/${product.id}`} size="sm" fw={500} lineClamp={2}>
          {product.name}
        </Anchor>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{product.brand || '-'}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" lineClamp={1}>
          {product.category || '-'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" ff="monospace">
          {product.barcode}
        </Text>
      </Table.Td>
      <Table.Td ta="center">
        <Text size="sm">{product.reviewCount}</Text>
      </Table.Td>
      <Table.Td ta="center">
        <Text size="sm">{product.averageRating?.toFixed(1) || '-'}</Text>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Products</Title>
        <Button
          leftSection={<IconRefresh size={16} />}
          onClick={() => setSeedModalOpen(true)}
        >
          Seed from OpenFoodFacts
        </Button>
      </Group>

      <TextInput
        placeholder="Search by name, brand, or barcode..."
        leftSection={<IconSearch size={16} />}
        rightSection={
          search ? (
            <IconX
              size={16}
              style={{ cursor: 'pointer' }}
              onClick={() => handleSearchChange('')}
            />
          ) : null
        }
        value={search}
        onChange={(e) => handleSearchChange(e.target.value)}
        w={400}
      />

      <Card shadow="sm" padding="lg" radius="md" withBorder w={250}>
        <Group>
          <ThemeIcon size={48} radius="md" variant="light" color="violet">
            <IconPackage size={24} />
          </ThemeIcon>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
              Total Products
            </Text>
            <Text size="xl" fw={700}>
              {pagination.total.toLocaleString()}
            </Text>
          </div>
        </Group>
      </Card>

      <Paper shadow="xs" radius="md" withBorder>
        {isLoading ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={70}>Image</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th w={150}>Brand</Table.Th>
                <Table.Th w={150}>Category</Table.Th>
                <Table.Th w={140}>Barcode</Table.Th>
                <Table.Th w={80} ta="center">Reviews</Table.Th>
                <Table.Th w={80} ta="center">Rating</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        )}
      </Paper>

      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Total {pagination.total} products
        </Text>
        <Pagination
          value={pagination.current}
          onChange={(p) => setPage(p)}
          total={pagination.totalPages}
        />
      </Group>

      {/* Seed Modal */}
      <Modal
        opened={seedModalOpen}
        onClose={() => {
          setSeedModalOpen(false);
          setSeedResult(null);
          seedForm.reset();
        }}
        title="Seed Products from OpenFoodFacts"
        size="lg"
      >
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="blue">
            This will fetch products from OpenFoodFacts. Products with matching barcodes that already exist will be skipped.
          </Alert>

          <NumberInput
            label="Number of pages to fetch"
            description="Each page fetches products from OpenFoodFacts by search"
            min={1}
            max={10}
            {...seedForm.getInputProps('pages')}
          />

          <NumberInput
            label="Products per page"
            description="Maximum products per API call"
            min={10}
            max={100}
            {...seedForm.getInputProps('pageSize')}
          />

          {seedResult && (
            <Alert icon={<IconCheck size={16} />} color="green" title="Seeding Complete">
              <Stack gap="xs">
                <Text size="sm">Added: <strong>{seedResult.addedCount}</strong> products</Text>
                <Text size="sm">Skipped: <strong>{seedResult.skippedCount}</strong> products (already exist)</Text>

                {seedResult.addedProducts.length > 0 && (
                  <Paper p="sm" bg="gray.0" radius="sm" mah={200} style={{ overflow: 'auto' }}>
                    <Text size="sm" fw={500} mb="xs">Recently Added:</Text>
                    {seedResult.addedProducts.slice(0, 20).map((p) => (
                      <Text key={p.id} size="xs" c="dimmed">
                        {p.name} {p.brand && `(${p.brand})`}
                      </Text>
                    ))}
                    {seedResult.addedProducts.length > 20 && (
                      <Text size="xs" c="dimmed">...and {seedResult.addedProducts.length - 20} more</Text>
                    )}
                  </Paper>
                )}
              </Stack>
            </Alert>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setSeedModalOpen(false)}>
              Close
            </Button>
            <Button
              onClick={handleSeedProducts}
              loading={seedMutation.isPending}
              leftSection={<IconRefresh size={16} />}
            >
              {seedMutation.isPending ? 'Seeding...' : 'Start Seeding'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
